import type { IUpdateCovidInfoCmd } from "@/data/commands/profile/UpdateCovidInfoCmd";
import { Factory } from "@/data/dao/Factory";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import { Validation } from "@/data/util/Validation";

import { logError } from "@/shared/logging/errors";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { DomUtil } from "@/shared/ui/Helper";
import { Ripple } from "@/shared/ui/Ripple";

import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { CovidCertEncrypter } from "@/app/elements/CovidCertEncrypter";
import { DateInput } from "@/app/elements/DateInput";
import { PhoneNumberInput } from "@/app/elements/PhoneNumberInput";
import { trans } from "@/app/localization/Trans";
import { CoronaSignupState, UserCovidCertificateService } from "@/app/services/userCovidCerificateService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export class CovidDataCompletion {
    public onDone?: { () };
    public onModeChanged?: { (mode: string) };
    public onCancel?: { () };

    private $element: JQuery;

    private $certificate: JQuery;
    private $contactTracing: JQuery;

    private phoneNumberInput?: PhoneNumberInput;
    private covidCertEncrypter?: CovidCertEncrypter;
    private birthdayInput?: DateInput;
    private userCovidCertificateService: UserCovidCertificateService;

    constructor() {
        this.$element = $('<div class="coviddatacompletion" />');
        this.$certificate = $('<div class="certificate" />').appendTo(this.$element);
        this.$contactTracing = $('<div class="tracing" />').appendTo(this.$element);

        this.userCovidCertificateService = UserCovidCertificateService.getInstance();
    }

    public renderTo($element: JQuery): CovidDataCompletion {
        this.$element.appendTo($element);

        return this;
    }

    public async show() {
        this.$certificate.hide();
        this.$contactTracing.hide();

        const loader = $(
            '<div class="loading"><div class="loader"></div> ' + trans.covid.validatingCertificate + "</div>"
        ).appendTo(this.$element);
        const state = await this.userCovidCertificateService.isCovidValid(Helper.getCovidServiceConfig()!);
        loader.remove();
        if (state === CoronaSignupState.Valid) {
            if (this.onDone !== undefined) this.onDone();
        } else if (state === CoronaSignupState.CertificateVerification) {
            this.showCertificate();
        } else if (state === CoronaSignupState.MissingData) {
            this.showContactTracing();
        }
    }

    private showCertificate() {
        this.$certificate.show();
        this.$contactTracing.hide();

        if (this.$certificate.children().length !== 0) return;

        const continueOrDone = async () => {
            const state = await this.userCovidCertificateService.isCovidValid(Helper.getCovidServiceConfig()!);
            if (state === CoronaSignupState.Valid) {
                if (this.onDone !== undefined) this.onDone();
            } else if (state === CoronaSignupState.CertificateVerification) {
                this.showCertificate();
            } else if (state === CoronaSignupState.MissingData) {
                this.showContactTracing();
            } else {
                this.show();
            }
        };

        this.covidCertEncrypter = new CovidCertEncrypter(
            Helper.getCovidServiceConfigValue("verifyCovidCertMode")!
        ).appendTo(this.$certificate);
        this.covidCertEncrypter.onVerified = () => continueOrDone();

        this.$certificate.append(
            '<div class="dataprotection"><i class="fas fa-shield-check"></i><div class="text">' +
                trans.covidSignupPopover.certDataProtection +
                "</div></div>"
        );

        const submitBtn = Ripple.bind(
            $('<button class="btn" disabled>' + trans.covidSignupPopover.verifyBtn + "</button>")
                .appendTo(this.$certificate)
                .click(async (e) => {
                    e.preventDefault();

                    if (!this.covidCertEncrypter!.isValid()) return;

                    submitBtn.addClass("loading").prop("disabled", true);
                    try {
                        await this.covidCertEncrypter!.encrypt();
                        submitBtn
                            .removeClass("loading")
                            .prop("disabled", false)
                            .html(trans.covidSignupPopover.verifyBtn);
                        continueOrDone();
                    } catch (ex) {
                        logError(ex);
                        submitBtn
                            .removeClass("loading")
                            .prop("disabled", false)
                            .html(trans.covidSignupPopover.verifyBtn);
                    }
                })
        );

        this.covidCertEncrypter.onProgressChanged = (e) => {
            switch (e.state) {
                case "uploading_files":
                    submitBtn.html(trans.covidSignupPopover.stateUploadingFiles);
                    break;
                case "validating_certificate":
                    submitBtn.html(trans.covidSignupPopover.stateValidatingCertificate);
                    break;
                case "validating_identity":
                    submitBtn.html(trans.covidSignupPopover.stateValidatingIdentity);
                    break;
                case "decrypt_and_store":
                    submitBtn.html(trans.covidSignupPopover.stateDecryptAndStore);
                    break;
                default:
                    submitBtn.html(trans.covidSignupPopover.verifyBtn);
            }
        };

        $('<a href="#" class="back">' + trans.covidSignupPopover.back + "</a>")
            .appendTo(this.$certificate)
            .click((e) => {
                e.preventDefault();

                if (this.onCancel !== undefined) this.onCancel();
            });

        this.covidCertEncrypter.onReadyStateChanged = (isReady) => {
            if (isReady) submitBtn.prop("disabled", false);
            else submitBtn.prop("disabled", true);
        };

        this.covidCertEncrypter.onSizeChanged = () => {
            if (this.onModeChanged !== undefined) this.onModeChanged("certificate");
        };

        if (this.onModeChanged !== undefined) this.onModeChanged("certificate");
    }

    private showContactTracing() {
        this.$contactTracing.show();
        this.$certificate.hide();

        if (this.$contactTracing.children().length !== 0) return;

        const form = $('<form class="form" novalidate />').appendTo(this.$contactTracing);

        let row: JQuery;
        let fNameField: JQuery;
        let lNameField: JQuery;

        const profile = TastierClient.identityContext.getProfile();
        if (
            this.userCovidCertificateService.getCovidCertificate() === undefined &&
            profile !== undefined &&
            profile.firstName === undefined
        ) {
            row = $('<div class="formrow" />').appendTo(form);
            fNameField = $(
                '<div class="formcontrol intent"><label>' +
                    trans.covidSignupPopover.firstName +
                    '</label><input type="text" name="firstname" value="" /></div>'
            )
                .appendTo(row)
                .find("> input");
        }
        if (
            this.userCovidCertificateService.getCovidCertificate() === undefined &&
            profile !== undefined &&
            profile.lastName === undefined
        ) {
            row = $('<div class="formrow" />').appendTo(form);
            lNameField = $(
                '<div class="formcontrol intent"><label>' +
                    trans.covidSignupPopover.lastName +
                    '</label><input type="text" name="lastname" value="" /></div>'
            )
                .appendTo(row)
                .find("> input");
        }

        if (profile !== undefined && profile.phoneNumber === undefined) {
            row = $('<div class="formrow" />').appendTo(form);
            this.phoneNumberInput = new PhoneNumberInput(true).appendTo(row);
        }

        let zipField: JQuery;
        if (profile !== undefined && profile.zip === undefined) {
            row = $('<div class="formrow" />').appendTo(form);
            zipField = $(
                '<div class="formcontrol intent"><label>' +
                    trans.signup.zip +
                    '</label><input type="text" name="zip" value="" /></div>'
            )
                .appendTo(row)
                .find("> input");
        }

        if (
            this.userCovidCertificateService.getCovidCertificate() !== undefined &&
            profile !== undefined &&
            profile.birthday === undefined
        ) {
            row = $('<div class="formrow" />').appendTo(form);
            this.birthdayInput = new DateInput(trans.covidSignupPopover.birthday, true, {
                startView: 2,
                defaultViewDate: new Date(new Date().getTime() - 86400000 * 30 * 365), // set default to 20 years ago,
                endDate: "today",
            }).appendTo(row);
        }

        form.append('<div class="disclaimer">' + trans.covidSignupPopover.disclaimer + "</div>");

        row = $('<div class="formrow" />').appendTo(form);
        const submitBtn = Ripple.bind(
            $('<button class="btn" type="submit">' + trans.covidSignupPopover.submit + "</button>")
                .appendTo(row)
                .click((e) => {
                    e.preventDefault();
                    form.submit();
                })
        );

        FloatLabel.bind(form);

        if (this.userCovidCertificateService.getCovidCertificate() === undefined) {
            $('<a href="#" class="back">' + trans.covidSignupPopover.back + "</a>")
                .appendTo(this.$contactTracing)
                .click((e) => {
                    e.preventDefault();

                    if (this.onCancel !== undefined) this.onCancel();
                });
        }

        form.submit(async (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (submitBtn.hasClass("loading")) return;

            const profileEditData: IUpdateCovidInfoCmd = {
                firstName: fNameField === undefined || fNameField.val().length === 0 ? undefined : fNameField.val(),
                lastName: lNameField === undefined || lNameField.val().length === 0 ? undefined : lNameField.val(),
                phoneNumber: this.phoneNumberInput === undefined ? undefined : this.phoneNumberInput.getNumber(),
                phoneCountryCode:
                    this.phoneNumberInput === undefined ? undefined : this.phoneNumberInput.getCountryCode(),
                birthday:
                    this.birthdayInput !== undefined && this.birthdayInput !== undefined
                        ? this.birthdayInput.getValue()
                        : undefined,
                zip: zipField !== undefined ? (zipField.val().length === 0 ? undefined : zipField.val()) : undefined,
            };

            let hasError = false;
            form.find(".has-error").removeClass("has-error");

            if (fNameField !== undefined) {
                if (
                    profileEditData.firstName === undefined ||
                    profileEditData.firstName.length < config.minNameLength ||
                    profileEditData.firstName.length > config.maxNameLength
                ) {
                    fNameField.parent().addClass("has-error");
                    hasError = true;
                }
            }

            if (lNameField !== undefined) {
                if (
                    profileEditData.lastName === undefined ||
                    profileEditData.lastName.length < config.minNameLength ||
                    profileEditData.lastName.length > config.maxNameLength
                ) {
                    lNameField.parent().addClass("has-error");
                    hasError = true;
                }
            }

            if (this.phoneNumberInput !== undefined) {
                if (
                    profileEditData.phoneNumber === undefined ||
                    profileEditData.phoneCountryCode === undefined ||
                    !this.phoneNumberInput.isValid()
                ) {
                    hasError = true;
                    this.phoneNumberInput.setError(true);
                }
            }

            if (this.birthdayInput !== undefined) {
                if (this.birthdayInput !== undefined) {
                    if (this.birthdayInput.getValue() === undefined) {
                        hasError = true;
                        this.birthdayInput.setError(true);
                    } else this.birthdayInput.setError(false);
                }
            }

            if (zipField !== undefined) {
                if (profileEditData.zip === undefined || !Validation.isZip(profileEditData.zip)) {
                    zipField.parent().addClass("has-error");
                    hasError = true;
                }
            }

            if (hasError) {
                const lastErrorField = form.find(".has-error").first();
                DomUtil.scrollTo(this.$contactTracing, lastErrorField);
                return;
            }

            submitBtn.addClass("loading").prop("disabled", true);
            try {
                const p = await Factory.getInstance<ProfileDao>(ProfileDao).updateCovidInfo(profileEditData);
                TastierClient.identityContext.aggregateIdentity(p, true);

                if (this.onDone !== undefined) this.onDone();
            } catch (ex) {
                logError(ex);
                Popup.show(trans.covidSignupPopover.failed);
                submitBtn.removeClass("loading").prop("disabled", false);
            }

            return false;
        });

        if (this.onModeChanged !== undefined) this.onModeChanged("tracing");
    }

    public destroy() {
        if (this.phoneNumberInput != undefined) {
            this.phoneNumberInput.destroy();
            this.phoneNumberInput = undefined;
        }
    }
}
